/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import { Button, Grid, Stack, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import NoData from '../../../../../../components/Common/NoData';
import { LanguageContext } from '../../../../../../libs/Context';
import { RiDeleteBinLine } from 'react-icons/ri';
import { MdOutlineEdit } from 'react-icons/md';
import NewDialogPopup from '../../../../../../components/Common/NewDialogPopup/NewDialogPopup';
import QuestionsTable from './Components/QuestionsTable';
import CreateSurvey from './Components/CreateSurvey';
import { catchErr, generateRandomUUID } from '../../../../../../libs/HelperContent';
import { createSurvey, deleteSurvey, getSurveyDetails } from '../../../../../actions/meeting_actions';
import { toast } from 'react-toastify';
import Loading from '../../../../../../components/Common/Loaders/FullWidthLoader/Loading';

const ViewQuestions = ({ initialState, handleAction, handleDeleteIndividualQuestion, language }) =>
{
	return (
		<Grid container px='var(--pixel16)' pb='6px' spacing={2}>
			<Stack direction={'row'} spacing={2} justifyContent={'end'} width={'var(--percent100)'}>
				<Tooltip title='Edit' placement='top' onClick={() => handleAction('openCreate', true)}>
					<Typography><MdOutlineEdit cursor={'pointer'} fontSize='var(--pixel22)'/></Typography>
				</Tooltip>
				<Tooltip title='Delete' placement='top'>
					<Typography><RiDeleteBinLine cursor={'pointer'} fontSize='var(--pixel20)' onClick={() => handleAction('toggleDelete', true)}/></Typography>
				</Tooltip>
			</Stack>
			<Grid item xs={12} p='var(--pixel8)'>
				<Stack direction='column' spacing={1}>
					<Typography variant='inherit' fontWeight='var(--font500)'>{initialState?.title}</Typography>
					<Typography variant='inherit'>{initialState?.description}</Typography>
				</Stack>
			</Grid>
			<Grid item xs={12} p='var(--pixel8)'>
				<TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', border: '1px solid #CFD1D4', borderRadius: 'var(--pixel4)' }}>
					<Table stickyHeader aria-label='sticky table'>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>{language?.questions}</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>{language?.type}</TableCell>
								<TableCell/>
								<TableCell/>
							</TableRow>
						</TableHead>
						<TableBody>
							{initialState?.questions?.map((survey, ind) => (
								<QuestionsTable
									key={survey?.id ?? survey?._id ?? ind}
									showDelete={initialState?.questions.length > 1}
									survey={survey}
									handleAction={handleAction}
									handleDelete={handleDeleteIndividualQuestion}
									index={ind}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
		</Grid>
	);
};

const SurveyDetails = (props) =>
{
	const { form, handleDeletePop, handleBack,
		handleSkipFinish, handleNextCallBack } = props;
	const { language } = useContext(LanguageContext);
	const [ action, setAction ] = useState({
		openCreate   : false,
		edit         : '',
		loading      : false,
		toggleDelete : false
	});
	const initialS = {
		title       : '',
		description : '',
		questions   : [],
		_id         : ''
	};
	const [ initialState, setInitialState ] = useState<any>(initialS);
	const { loading } = action;
	const openCreate = action?.openCreate || action.edit;

	const getQuesList = async () =>
	{
		setAction({ ...action, loading: true });
		try
		{
			const query:any = {
				sessionId : form?.id
			};

			const res = await getSurveyDetails(query);

			if (res?.status)
			{
				setInitialState((prev) => ({ ...prev,
					title       : res?.data?.title || '',
					description : res?.data?.description || '',
					questions   : res?.data?.questions || [],
					_id         : res?.data?._id || ''
				}));
			}
		}
		catch (err)
		{
			console.log('---------------', err);
		}
		setAction({ ...action, loading: false });
	};

	const handleAction = (key, value) =>
	{
		if (typeof key === 'string')
		{
			setAction((prev) => ({ ...prev, [key]: value }));
		}
		else if (typeof key === 'object')
		{
			setAction((prev) => ({ ...prev, ...key }));
		}
	};

	const handleConfirmDeleteSurvey = async () =>
	{
		try
		{
			setAction({ ...action, loading: true });
			const query = {
				sessionId : form?.id
			};
			const res = await deleteSurvey(query);

			if (res?.status)
			{
				toast.success(language?.surveyDeletedSuccess, {
					position  : toast.POSITION.TOP_RIGHT,
					className : 'toast-message'
				});

				setInitialState(initialS);
				handleAction('toggleDelete', false);
			}
		}
		catch (err)
		{
			catchErr(err);
		}
		setAction((prev) => ({ ...prev, loading: false }));
	};

	useEffect(() =>
	{
		getQuesList();
	}, []);

	const handleNext = async () =>
	{
		handleNextCallBack();
	};

	const handleDeleteIndividualQuestion = async (ind) =>
	{
		const newQuestionList = [ ...(initialState?.questions ?? []) ];

		newQuestionList.splice(ind, 1);

		try
		{
			const body = {
				sessionId   : form?.id,
				title       : initialState?.title,
				description : initialState?.description,
				questions   : newQuestionList
			};

			if (newQuestionList.length > 0)
			{
				const res = await createSurvey(body);

				if (res?.status)
				{
					toast.success(language?.questionDeletedSuccess, {
						position  : toast.POSITION.TOP_RIGHT,
						className : 'toast-message'
					});
				}
				setInitialState((prev) => ({ ...prev, questions: newQuestionList }));
			}
		}
		catch (err)
		{
			catchErr(err);
		}
	};

	return (
		<>
			<Stack direction='column' height='calc(100% - 56px)'>
				{loading && <Loading/>}
				{!loading && !openCreate && initialState?.questions?.length > 0 &&
				<ViewQuestions
					initialState={initialState}
					handleAction={handleAction}
					handleDeleteIndividualQuestion={handleDeleteIndividualQuestion}
					language={language}
				/>}
				{openCreate && (
					<CreateSurvey
						initialState={initialState}
						setInitialState={setInitialState}
						handleAction={handleAction}
						form={form}
						editId={action?.edit}
					/>
				)}
				{!openCreate && initialState?.questions?.length === 0 &&
				<Stack direction='column' alignItems='center' height='var(--percent100)' justifyContent='center'>
					<NoData noDataHead={language?.NoSurveyAdd}
						src1={'/images/icons/webSurvey.svg'}
						color='pdngTSM fontWeightSM'
					/>
					<Button color='primary' variant='outlined' disableElevation onClick={() => handleAction('openCreate', true)}>
						{language?.createSurvey}
					</Button>
				</Stack>
				}
			</Stack>
			{!openCreate &&
			<Grid container justifyContent={'space-between'} alignItems='center' p='var(--pixel16)'>
				<Stack direction='row' alignItems='center' spacing={1}>
					<Button variant='outlined' onClick={handleBack}>
						{language?.previous}
					</Button>
					<Button onClick={handleSkipFinish} variant='outlined'>
						{language?.skipandFinish}
					</Button>
				</Stack>
				<Grid item>
					<Stack direction='row' spacing={1}>
						<Button onClick={handleDeletePop} variant='outlined'>
							{language?.cancel}
						</Button>
						<Button variant='contained' sx={{ color: 'var(--whiteColor)' }} onClick={handleNext}>
							{language?.next}
						</Button>
					</Stack>
				</Grid>
			</Grid>
			}
			{action?.toggleDelete &&
			<NewDialogPopup
				open={action?.toggleDelete}
				handleConfirm={() => handleAction('toggleDelete', false)}
				closeCallBack={handleConfirmDeleteSurvey}
				type={language?.no}
				id={undefined}
				label={language?.survaydel}
				from='feedCreate'
				formatType={language?.yes}
			/>}
		</>
	);
};

export default SurveyDetails;
